/**
 * @file CountryFlag.tsx
 * @brief Circular flag image for a country row (flagcdn.com).
 *
 * @author DES
 * @date 19-06-2026
 */

import { cn } from "@/shared/theme/cn";

interface CountryFlagProps {
  code: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZE: Record<NonNullable<CountryFlagProps["size"]>, string> = {
  sm: "h-5 w-5",
  md: "h-10 w-10",
  lg: "h-14 w-14",
};

// Module-private: no external consumer, and a value export beside a component
// costs the module its Fast Refresh. (02-08-2026)
function countryFlagUrl(code: string, width = 80): string {
  return `https://flagcdn.com/w${width}/${code.toLowerCase()}.png`;
}

export function CountryFlag({
  code,
  size = "md",
  className,
}: CountryFlagProps) {
  if (!code || code.length !== 2) return null;

  const px = size === "sm" ? 40 : size === "lg" ? 160 : 80;

  return (
    <span
      className={cn(
        // Light edge so circular flags read clearly on light/dark rows (13-07-2026).
        // Matches LanguageFlag border treatment.
        "inline-flex shrink-0 overflow-hidden rounded-full border border-border/80 bg-muted/30",
        SIZE[size],
        className,
      )}
    >
      <img
        src={countryFlagUrl(code, px)}
        alt=""
        aria-hidden
        draggable={false}
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </span>
  );
}
